import { useEffect, useRef } from 'react';
import * as d3 from 'd3';

const SYMBOLIC = '#DCB13C';
const TEXT_MUTED = '#5a5f7d';

interface ValueItem {
  label: string;
  meaningHeight: number;
  marketHeight: number;
}

export function ProxyVsReality() {
  const svgRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const width = 320;
    const barW = 28;
    const leftX = 70;
    const rightX = 222;
    const baseY = 175;

    const items: ValueItem[] = [
      { label: 'care', meaningHeight: 55, marketHeight: 18 },
      { label: 'wisdom', meaningHeight: 48, marketHeight: 12 },
      { label: 'connection', meaningHeight: 42, marketHeight: 35 },
    ];

    // Stacked bar positions
    let leftOffset = 0;
    let rightOffset = 0;
    const leftSegments = items.map((item) => {
      const seg = { ...item, y: baseY - leftOffset - item.meaningHeight, h: item.meaningHeight };
      leftOffset += item.meaningHeight + 2;
      return seg;
    });
    const rightSegments = items.map((item) => {
      const seg = { ...item, y: baseY - rightOffset - item.marketHeight, h: item.marketHeight };
      rightOffset += item.marketHeight + 2;
      return seg;
    });

    // Column headers
    svg.append('text')
      .attr('x', leftX + barW / 2)
      .attr('y', 18)
      .attr('text-anchor', 'middle')
      .attr('font-size', 9)
      .attr('font-weight', 700)
      .attr('class', 'viz-text')
      .text('Meaning Value');

    svg.append('text')
      .attr('x', rightX + barW / 2)
      .attr('y', 18)
      .attr('text-anchor', 'middle')
      .attr('font-size', 9)
      .attr('font-weight', 700)
      .attr('class', 'viz-text')
      .text('Market Value');

    // Color scale for items
    const colors = [SYMBOLIC, '#c9a234', '#b89530'];

    // Draw left bar segments
    leftSegments.forEach((seg, i) => {
      svg.append('rect')
        .attr('x', leftX)
        .attr('y', baseY)
        .attr('width', barW)
        .attr('height', 0)
        .attr('rx', 2)
        .attr('fill', colors[i])
        .attr('opacity', 0.8)
        .transition()
        .duration(500)
        .delay(i * 150)
        .attr('y', seg.y)
        .attr('height', seg.h);

      svg.append('text')
        .attr('x', leftX - 8)
        .attr('y', seg.y + seg.h / 2 + 3)
        .attr('text-anchor', 'end')
        .attr('font-size', 8)
        .attr('class', 'viz-text-muted')
        .attr('opacity', 0)
        .text(seg.label)
        .transition()
        .duration(300)
        .delay(400 + i * 150)
        .attr('opacity', 1);
    });

    // Draw right bar segments
    rightSegments.forEach((seg, i) => {
      svg.append('rect')
        .attr('x', rightX)
        .attr('y', baseY)
        .attr('width', barW)
        .attr('height', 0)
        .attr('rx', 2)
        .attr('fill', colors[i])
        .attr('opacity', 0.55)
        .transition()
        .duration(500)
        .delay(600 + i * 150)
        .attr('y', seg.y)
        .attr('height', seg.h);

      svg.append('text')
        .attr('x', rightX + barW + 8)
        .attr('y', seg.y + seg.h / 2 + 3)
        .attr('text-anchor', 'start')
        .attr('font-size', 8)
        .attr('class', 'viz-text-muted')
        .attr('opacity', 0)
        .text(seg.label)
        .transition()
        .duration(300)
        .delay(800 + i * 150)
        .attr('opacity', 1);
    });

    // Draw connecting lines that warp
    const connectorGroup = svg.append('g');

    const warpTimer = d3.timeout(() => {
      leftSegments.forEach((lSeg, i) => {
        const rSeg = rightSegments[i];
        const ly = lSeg.y + lSeg.h / 2;
        const ry = rSeg.y + rSeg.h / 2;
        const lx = leftX + barW;
        const rx = rightX;
        const midX = (lx + rx) / 2;

        // Create curved path from left to right
        const path = connectorGroup
          .append('path')
          .attr('d', `M ${lx},${ly} C ${midX},${ly} ${midX},${ly} ${rx},${ly}`)
          .attr('fill', 'none')
          .attr('stroke', colors[i])
          .attr('stroke-width', 1.5)
          .attr('stroke-dasharray', '4,3')
          .attr('opacity', 0);

        // Animate: appear then warp
        path
          .transition()
          .duration(400)
          .attr('opacity', 0.6)
          .transition()
          .duration(600)
          .ease(d3.easeCubicInOut)
          .attr('d', `M ${lx},${ly} C ${midX},${ly + (ry - ly) * 0.2} ${midX},${ry - (ry - ly) * 0.2} ${rx},${ry}`)
          .transition()
          .duration(400)
          .attr('d', `M ${lx},${ly} C ${midX + 10},${(ly + ry) / 2 + 8} ${midX - 10},${(ly + ry) / 2 - 8} ${rx},${ry}`)
          .attr('opacity', 0.5);
      });
    }, 1200);

    // Add "distortion" label
    const labelTimer = d3.timeout(() => {
      svg.append('text')
        .attr('x', width / 2)
        .attr('y', baseY - 60)
        .attr('text-anchor', 'middle')
        .attr('font-size', 8)
        .attr('font-style', 'italic')
        .attr('fill', TEXT_MUTED)
        .attr('opacity', 0)
        .text('distortion')
        .transition()
        .duration(400)
        .attr('opacity', 0.7);
    }, 2600);

    return () => {
      warpTimer.stop();
      labelTimer.stop();
      svg.selectAll('*').remove();
    };
  }, []);

  return (
    <div className="viz-container">
      <h4 className="viz-container__title">
        <i className="fa-solid fa-money-bill" /> Money &mdash; Value Proxy Distortion
      </h4>
      <svg ref={svgRef} viewBox="0 0 320 200" />
      <p className="viz-container__caption">
        The proxy starts answering questions it can&rsquo;t.
      </p>
    </div>
  );
}
